import React, { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
    ArrowLeft,
    Edit3,
    Download,
    ExternalLink,
    Eye,
    Code2,
    FileText,
    Globe,
    Rocket,
    Settings,
    Smartphone,
    Monitor,
    Tablet,
    Play,
    Pause
} from 'lucide-react'
import ReactMarkdown from 'react-markdown'

const AppRenderer = ({ appData, onBack, onEdit }) => {
    const [viewMode, setViewMode] = useState('preview') // preview, code, files
    const [deviceView, setDeviceView] = useState('desktop') // desktop, tablet, mobile
    const [selectedFile, setSelectedFile] = useState(null)
    const [isDeploying, setIsDeploying] = useState(false)
    const [deploymentUrl, setDeploymentUrl] = useState(null)
    const iframeRef = useRef(null)

    // 🤖 Extract AI-generated code from pipeline results
    const aiGeneratedCode = appData?.pipelineResults?.reduce((acc, result) => {
        if (result?.output?.generated_code) {
            return { ...acc, ...result.output.generated_code }
        }
        return acc
    }, {}) || {}

    const handleDeploy = async () => {
        setIsDeploying(true)

        // Simulate deployment process
        setTimeout(() => {
            const deployUrl = `https://app-${Date.now()}.deepnx-apps.dev`
            setDeploymentUrl(deployUrl)
            setIsDeploying(false)
        }, 3000)
    }

    const deviceSizes = {
        desktop: 'w-full',
        tablet: 'w-3/4 max-w-2xl',
        mobile: 'w-80'
    }

    // 🤖 Use AI-generated code if available, otherwise fallback
    const appCode = Object.keys(aiGeneratedCode).length > 0 ? aiGeneratedCode : {
        'App.jsx': `// 🤖 AI-Generated Application
// Based on: ${appData?.originalPrompt || 'User Request'}

import React, { useState, useEffect } from 'react'

function App() {
  const [isLoading, setIsLoading] = useState(true)
  const [data, setData] = useState(null)

  useEffect(() => {
    // Simulate app initialization
    setTimeout(() => {
      setData({ message: 'AI-Generated App Ready!' })
      setIsLoading(false)
    }, 1000)
  }, [])

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Loading AI-generated application...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <header className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">
          🤖 AI-Generated Application
        </h1>
        <p className="text-gray-600">
          Generated from: "${appData?.originalPrompt || 'User Request'}"
        </p>
      </header>
      
      <main className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            Application Features
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            ${appData?.pipelineResults?.map(result =>
            result?.output?.features_implemented?.map(feature =>
                `<div key="${feature}" className="p-4 bg-blue-50 rounded-lg">
                  <span className="text-blue-800 font-medium">${feature}</span>
                </div>`
            ).join('\n            ') || ''
        ).join('\n            ') || `
            <div className="p-4 bg-blue-50 rounded-lg">
              <span className="text-blue-800 font-medium">Modern React Architecture</span>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <span className="text-green-800 font-medium">Responsive Design</span>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <span className="text-purple-800 font-medium">Tailwind CSS Styling</span>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg">
              <span className="text-orange-800 font-medium">AI-Powered Generation</span>
            </div>`}
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h3 className="text-xl font-semibold mb-4 text-gray-800">
            Generated Components
          </h3>
          <div className="space-y-2">
            ${Object.keys(aiGeneratedCode).map(filename =>
            `<div key="${filename}" className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-mono text-sm text-gray-700">${filename}</span>
                <span className="text-xs text-gray-500">AI Generated</span>
              </div>`
        ).join('\n            ') || `
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="font-mono text-sm text-gray-700">App.jsx</span>
              <span className="text-xs text-green-600">Main Component</span>
            </div>`}
          </div>
        </div>
      </main>
    </div>
  )
}

export default App`,

        'components/Dashboard.jsx': `import React, { useState, useEffect } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchDashboardStats()
  }, [])

  const fetchDashboardStats = async () => {
    // Mock API call
    setTimeout(() => {
      setStats([
        { name: 'Jan', value: 400 },
        { name: 'Feb', value: 300 },
        { name: 'Mar', value: 600 },
        { name: 'Apr', value: 800 },
        { name: 'May', value: 500 },
      ])
      setIsLoading(false)
    }, 1000)
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">
          Welcome back, {user?.name}!
        </h1>
        <button className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">
          New Project
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-700">Total Projects</h3>
          <p className="text-3xl font-bold text-blue-600">12</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-700">Active Users</h3>
          <p className="text-3xl font-bold text-green-600">1,234</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-700">Revenue</h3>
          <p className="text-3xl font-bold text-purple-600">$45,678</p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Analytics Overview</h2>
        {isLoading ? (
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

export default Dashboard`,

        'server.js': `const express = require('express')
const cors = require('cors')
const jwt = require('jsonwebtoken')
const bcrypt = require('bcryptjs')
const { Pool } = require('pg')

const app = express()
const PORT = process.env.PORT || 5000

// Middleware
app.use(cors())
app.use(express.json())

// Database connection
const pool = new Pool({
  connectionString: process.env.DATABASE_URL,
  ssl: process.env.NODE_ENV === 'production' ? { rejectUnauthorized: false } : false
})

// Auth middleware
const authenticateToken = (req, res, next) => {
  const authHeader = req.headers['authorization']
  const token = authHeader && authHeader.split(' ')[1]

  if (!token) {
    return res.sendStatus(401)
  }

  jwt.verify(token, process.env.JWT_SECRET, (err, user) => {
    if (err) return res.sendStatus(403)
    req.user = user
    next()
  })
}

// Routes
app.post('/api/auth/login', async (req, res) => {
  try {
    const { email, password } = req.body
    
    const userResult = await pool.query('SELECT * FROM users WHERE email = $1', [email])
    const user = userResult.rows[0]

    if (!user || !await bcrypt.compare(password, user.password)) {
      return res.status(401).json({ error: 'Invalid credentials' })
    }

    const token = jwt.sign(
      { id: user.id, email: user.email },
      process.env.JWT_SECRET,
      { expiresIn: '24h' }
    )

    res.json({
      token,
      user: {
        id: user.id,
        name: user.name,
        email: user.email
      }
    })
  } catch (error) {
    console.error('Login error:', error)
    res.status(500).json({ error: 'Internal server error' })
  }
})

app.get('/api/user/profile', authenticateToken, async (req, res) => {
  try {
    const userResult = await pool.query(
      'SELECT id, name, email, created_at FROM users WHERE id = $1',
      [req.user.id]
    )
    
    if (userResult.rows.length === 0) {
      return res.status(404).json({ error: 'User not found' })
    }

    res.json(userResult.rows[0])
  } catch (error) {
    console.error('Profile fetch error:', error)
    res.status(500).json({ error: 'Internal server error' })
  }
})

app.get('/api/dashboard/stats', authenticateToken, async (req, res) => {
  try {
    // Mock stats for demo
    const stats = {
      totalProjects: 12,
      activeUsers: 1234,
      revenue: 45678,
      chartData: [
        { name: 'Jan', value: 400 },
        { name: 'Feb', value: 300 },
        { name: 'Mar', value: 600 },
        { name: 'Apr', value: 800 },
        { name: 'May', value: 500 },
      ]
    }
    
    res.json(stats)
  } catch (error) {
    console.error('Stats fetch error:', error)
    res.status(500).json({ error: 'Internal server error' })
  }
})

app.listen(PORT, () => {
  console.log(\`Server running on port \${PORT}\`)
})`,

        'schema.sql': `-- Users table
CREATE TABLE users (
  id SERIAL PRIMARY KEY,
  name VARCHAR(255) NOT NULL,
  email VARCHAR(255) UNIQUE NOT NULL,
  password VARCHAR(255) NOT NULL,
  avatar_url VARCHAR(500),
  email_verified BOOLEAN DEFAULT FALSE,
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

-- Projects table
CREATE TABLE projects (
  id SERIAL PRIMARY KEY,
  user_id INTEGER REFERENCES users(id) ON DELETE CASCADE,
  name VARCHAR(255) NOT NULL,
  description TEXT,
  status VARCHAR(50) DEFAULT 'active',
  settings JSONB DEFAULT '{}',
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
  updated_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

-- Analytics table
CREATE TABLE analytics (
  id SERIAL PRIMARY KEY,
  project_id INTEGER REFERENCES projects(id) ON DELETE CASCADE,
  event_type VARCHAR(100) NOT NULL,
  event_data JSONB DEFAULT '{}',
  user_agent TEXT,
  ip_address INET,
  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP
);

-- Indexes for performance
CREATE INDEX idx_users_email ON users(email);
CREATE INDEX idx_projects_user_id ON projects(user_id);
CREATE INDEX idx_analytics_project_id ON analytics(project_id);
CREATE INDEX idx_analytics_event_type ON analytics(event_type);
CREATE INDEX idx_analytics_created_at ON analytics(created_at);

-- Sample data
INSERT INTO users (name, email, password) VALUES
('John Doe', 'john@example.com', '$2b$10$hash_here'),
('Jane Smith', 'jane@example.com', '$2b$10$hash_here');

INSERT INTO projects (user_id, name, description) VALUES
(1, 'E-commerce Platform', 'Full-featured online store with AI recommendations'),
(1, 'Analytics Dashboard', 'Real-time business intelligence platform'),
(2, 'Social Media App', 'Community platform with live chat features');`
    }

    const generatePreviewHTML = () => {
        return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${appData.name}</title>
    <script src="https://cdn.tailwindcss.com"></script>
    <style>
        body { font-family: 'Inter', system-ui, sans-serif; }
        .gradient-bg { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); }
        .glass { backdrop-filter: blur(10px); background: rgba(255, 255, 255, 0.1); border: 1px solid rgba(255, 255, 255, 0.2); }
    </style>
</head>
<body class="bg-gray-50">
    <!-- Header -->
    <header class="gradient-bg text-white shadow-lg">
        <div class="container mx-auto px-6 py-4">
            <div class="flex justify-between items-center">
                <div class="flex items-center space-x-4">
                    <div class="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                        <span class="text-xl">🚀</span>
                    </div>
                    <h1 class="text-2xl font-bold">${appData.name}</h1>
                </div>
                <nav class="hidden md:flex space-x-6">
                    <a href="#" class="hover:text-blue-200 transition-colors">Dashboard</a>
                    <a href="#" class="hover:text-blue-200 transition-colors">Projects</a>
                    <a href="#" class="hover:text-blue-200 transition-colors">Analytics</a>
                    <a href="#" class="hover:text-blue-200 transition-colors">Settings</a>
                </nav>
                <div class="flex items-center space-x-4">
                    <div class="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                        <span class="text-sm">👤</span>
                    </div>
                </div>
            </div>
        </div>
    </header>

    <!-- Main Content -->
    <main class="container mx-auto px-6 py-8">
        <!-- Welcome Section -->
        <div class="mb-8">
            <h2 class="text-3xl font-bold text-gray-800 mb-2">Welcome back, John!</h2>
            <p class="text-gray-600">Here's what's happening with your projects today.</p>
        </div>

        <!-- Stats Cards -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div class="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
                <div class="flex items-center justify-between">
                    <div>
                        <h3 class="text-sm font-medium text-gray-500">Total Projects</h3>
                        <p class="text-2xl font-bold text-gray-900">12</p>
                    </div>
                    <div class="text-blue-500">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"></path>
                        </svg>
                    </div>
                </div>
            </div>
            
            <div class="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
                <div class="flex items-center justify-between">
                    <div>
                        <h3 class="text-sm font-medium text-gray-500">Active Users</h3>
                        <p class="text-2xl font-bold text-gray-900">1,234</p>
                    </div>
                    <div class="text-green-500">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                    </div>
                </div>
            </div>
            
            <div class="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500">
                <div class="flex items-center justify-between">
                    <div>
                        <h3 class="text-sm font-medium text-gray-500">Revenue</h3>
                        <p class="text-2xl font-bold text-gray-900">$45,678</p>
                    </div>
                    <div class="text-purple-500">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M12 2l3.09 6.26L22 9l-5 4.74L18.18 20 12 16.77 5.82 20 7 13.74 2 9l6.91-.74L12 2z"></path>
                        </svg>
                    </div>
                </div>
            </div>
        </div>

        <!-- Chart Section -->
        <div class="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h3 class="text-lg font-semibold text-gray-800 mb-4">Analytics Overview</h3>
            <div class="h-64 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg flex items-center justify-center">
                <div class="text-center">
                    <div class="text-4xl mb-2">📊</div>
                    <p class="text-gray-600">Interactive Chart Component</p>
                    <p class="text-sm text-gray-500">Real-time data visualization</p>
                </div>
            </div>
        </div>

        <!-- Recent Activity -->
        <div class="bg-white rounded-xl shadow-lg p-6">
            <h3 class="text-lg font-semibold text-gray-800 mb-4">Recent Activity</h3>
            <div class="space-y-4">
                <div class="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                    <div class="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white">
                        <span class="text-sm">📝</span>
                    </div>
                    <div class="flex-1">
                        <p class="font-medium text-gray-800">New project created</p>
                        <p class="text-sm text-gray-600">E-commerce platform with AI features</p>
                    </div>
                    <span class="text-sm text-gray-500">2 hours ago</span>
                </div>
                
                <div class="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                    <div class="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white">
                        <span class="text-sm">✅</span>
                    </div>
                    <div class="flex-1">
                        <p class="font-medium text-gray-800">Pipeline completed</p>
                        <p class="text-sm text-gray-600">Authentication system deployed</p>
                    </div>
                    <span class="text-sm text-gray-500">4 hours ago</span>
                </div>
                
                <div class="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                    <div class="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white">
                        <span class="text-sm">🚀</span>
                    </div>
                    <div class="flex-1">
                        <p class="font-medium text-gray-800">App deployed successfully</p>
                        <p class="text-sm text-gray-600">Social media dashboard is now live</p>
                    </div>
                    <span class="text-sm text-gray-500">6 hours ago</span>
                </div>
            </div>
        </div>
    </main>

    <!-- Footer -->
    <footer class="bg-gray-800 text-white py-8 mt-12">
        <div class="container mx-auto px-6 text-center">
            <p class="text-gray-400">Generated by DeepNX AI Pipeline System</p>
        </div>
    </footer>

    <script>
        // Add some interactivity
        document.querySelectorAll('a[href="#"]').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault()
                alert('Navigation would work in the deployed app!')
            })
        })
        
        // Simulate real-time updates
        setInterval(() => {
            const users = document.querySelector('.text-2xl.font-bold.text-gray-900')
            if (users && users.textContent.includes(',')) {
                const current = parseInt(users.textContent.replace(',', ''))
                users.textContent = (current + Math.floor(Math.random() * 10)).toLocaleString()
            }
        }, 5000)
    </script>
</body>
</html>`
    }

    return (
        <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
                <motion.button
                    onClick={onBack}
                    className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
                    whileHover={{ x: -5 }}
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span>Back</span>
                </motion.button>

                <div className="text-center">
                    <h2 className="text-2xl font-bold gradient-text">{appData.name}</h2>
                    <p className="text-gray-600">Generated App Preview</p>
                </div>

                <div className="flex items-center space-x-3">
                    <motion.button
                        onClick={onEdit}
                        className="btn-secondary"
                        whileHover={{ scale: 1.05 }}
                    >
                        <Edit3 className="w-4 h-4 mr-2" />
                        Edit
                    </motion.button>

                    <motion.button
                        onClick={handleDeploy}
                        disabled={isDeploying}
                        className={`btn-primary ${isDeploying ? 'opacity-50 cursor-not-allowed' : ''}`}
                        whileHover={!isDeploying ? { scale: 1.05 } : {}}
                    >
                        {isDeploying ? (
                            <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                                Deploying...
                            </>
                        ) : (
                            <>
                                <Rocket className="w-4 h-4 mr-2" />
                                Deploy App
                            </>
                        )}
                    </motion.button>
                </div>
            </div>

            {/* View Mode Selector */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-2 glass-effect rounded-lg p-1">
                    <button
                        onClick={() => setViewMode('preview')}
                        className={`px-4 py-2 rounded-md transition-all ${viewMode === 'preview' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-white/50'
                            }`}
                    >
                        <Eye className="w-4 h-4 inline mr-2" />
                        Preview
                    </button>
                    <button
                        onClick={() => setViewMode('code')}
                        className={`px-4 py-2 rounded-md transition-all ${viewMode === 'code' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-white/50'
                            }`}
                    >
                        <Code2 className="w-4 h-4 inline mr-2" />
                        Code
                    </button>
                    <button
                        onClick={() => setViewMode('files')}
                        className={`px-4 py-2 rounded-md transition-all ${viewMode === 'files' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-white/50'
                            }`}
                    >
                        <FileText className="w-4 h-4 inline mr-2" />
                        Files
                    </button>
                </div>

                {viewMode === 'preview' && (
                    <div className="flex items-center space-x-2 glass-effect rounded-lg p-1">
                        <button
                            onClick={() => setDeviceView('desktop')}
                            className={`p-2 rounded-md transition-all ${deviceView === 'desktop' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-white/50'
                                }`}
                        >
                            <Monitor className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setDeviceView('tablet')}
                            className={`p-2 rounded-md transition-all ${deviceView === 'tablet' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-white/50'
                                }`}
                        >
                            <Tablet className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setDeviceView('mobile')}
                            className={`p-2 rounded-md transition-all ${deviceView === 'mobile' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-white/50'
                                }`}
                        >
                            <Smartphone className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>

            {/* Content Area */}
            <motion.div
                key={viewMode}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-effect rounded-2xl overflow-hidden"
            >
                {viewMode === 'preview' && (
                    <div className="flex justify-center p-8 bg-gradient-to-br from-gray-100 to-gray-200">
                        <div className={`${deviceSizes[deviceView]} transition-all duration-500`}>
                            <div className="bg-white rounded-lg shadow-2xl overflow-hidden">
                                {deviceView !== 'desktop' && (
                                    <div className="h-6 bg-gray-800 flex items-center justify-center space-x-2">
                                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                        <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    </div>
                                )}
                                <iframe
                                    ref={iframeRef}
                                    srcDoc={generatePreviewHTML()}
                                    className="w-full h-96 border-0"
                                    title="App Preview"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {viewMode === 'code' && (
                    <div className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-800">Generated Code</h3>
                            <div className="flex items-center space-x-2">
                                {Object.keys(appCode).map(file => (
                                    <button
                                        key={file}
                                        onClick={() => setSelectedFile(file)}
                                        className={`px-3 py-1 rounded-md text-sm transition-all ${selectedFile === file || (!selectedFile && file === 'App.jsx')
                                            ? 'bg-primary text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {file}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="code-block">
                            <pre className="text-sm overflow-x-auto">
                                <code>{appCode[selectedFile || 'App.jsx']}</code>
                            </pre>
                        </div>
                    </div>
                )}

                {viewMode === 'files' && (
                    <div className="p-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Generated Files</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            {appData.files?.map((file, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                    className="bg-white rounded-lg p-4 shadow border-l-4 border-primary"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="font-semibold text-gray-800">{file.name}</h4>
                                            <p className="text-sm text-gray-600">{file.type} • {file.size}</p>
                                        </div>
                                        <motion.button
                                            whileHover={{ scale: 1.1 }}
                                            className="p-2 rounded-lg hover:bg-gray-100"
                                        >
                                            <Download className="w-4 h-4 text-gray-600" />
                                        </motion.button>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                )}
            </motion.div>

            {/* Deployment Success */}
            <AnimatePresence>
                {deploymentUrl && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                    >
                        <div className="glass-effect rounded-2xl p-8 max-w-md mx-4">
                            <div className="text-center">
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4"
                                >
                                    <Rocket className="w-8 h-8 text-white" />
                                </motion.div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">
                                    🎉 App Deployed Successfully!
                                </h3>
                                <p className="text-gray-600 mb-4">
                                    Your app is now live and accessible worldwide.
                                </p>
                                <div className="bg-gray-100 rounded-lg p-3 mb-4">
                                    <p className="text-sm text-gray-600 mb-1">Live URL:</p>
                                    <a
                                        href={deploymentUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-primary font-medium hover:underline flex items-center justify-center"
                                    >
                                        {deploymentUrl}
                                        <ExternalLink className="w-4 h-4 ml-1" />
                                    </a>
                                </div>
                                <button
                                    onClick={() => setDeploymentUrl(null)}
                                    className="btn-primary"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default AppRenderer